import { useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateConnection,
  useEditConnection,
} from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { RedshiftEnvironmentConfig } from "src/connections/config/database/redshift/RedshiftEnvironmentConfig";
import { DatabasePermissionsPill } from "src/connections/config/database/shared/DatabasePermissionsPill";
import { EnableNonProdConfigsField } from "src/connections/config/EnableNonProdConfigsField";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { EnvironmentTabs } from "src/connections/config/shared/EnvironmentTabs";
import { errorProps } from "src/connections/config/shared/errorProps";
import {
  convertBEConnectionToRedshiftConnectionConfigInputs,
  convertRedshiftConnectionConfigInputsToBEConnection,
  getRedshiftConnectionConfigInputsDefaultValues,
} from "src/connections/model/redshift";
import { RedshiftConnectionConfigInputs } from "src/connections/types";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  onClose: () => void;
  workspaceUrl: string;
  connection?: Connection;
};

export const RedshiftConnectionConfigForm: React.FC<Props> = ({
  onClose,
  workspaceUrl,
  connection,
}) => {
  const defaultValues = useMemo(() => {
    if (connection) {
      return convertBEConnectionToRedshiftConnectionConfigInputs(connection);
    } else {
      return getRedshiftConnectionConfigInputsDefaultValues();
    }
  }, [connection]);

  const formMethods = useForm<RedshiftConnectionConfigInputs>({
    defaultValues,
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = formMethods;

  const enableNonProdConfigs = watch("enableNonProdConfigs");

  const createConnectionMutation = useCreateConnection(workspaceUrl);
  const editConnectionMutation = useEditConnection(
    workspaceUrl,
    connection?.id,
  );

  const onSubmit = async (data: RedshiftConnectionConfigInputs) => {
    const payload = convertRedshiftConnectionConfigInputsToBEConnection(data);
    try {
      if (connection) {
        await editConnectionMutation.mutateAsync({
          id: connection.id,
          payload,
        });
      } else {
        await createConnectionMutation.mutateAsync(payload);
      }

      const action = connection ? "updated" : "created";
      toastActions.success({
        title: `Connection ${action}`,
        description: `Connection ${data.name} has been ${action}.`,
        withSidebar: true,
      });
      onClose();
    } catch {
      toastActions.failure({
        title: "Error",
        description: "We couldn't save your connection",
        withSidebar: true,
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <FormItem
            description="A name that describes the new connection"
            gap="sm"
            label="Connection name"
            isRequired
          >
            {Boolean(errors.name) && (
              <ErrorHint>{errors.name?.message}</ErrorHint>
            )}
            <Input
              data-loc="redshift-connection-name"
              placeholder="e.g. Redshift"
              fullWidth
              {...errorProps(isFieldErrored(errors, "name"))}
              {...register("name", {
                required: "Connection name is required",
                validate: validateNotStartsWithInternalPrefix(
                  `Connection name cannot start with ${taktileInternalPrefix}`,
                ),
              })}
            />
          </FormItem>
          <EnableNonProdConfigsField<RedshiftConnectionConfigInputs>
            dataLoc="redshift-connection-env-aware-switch"
            subtitle="You can set different credentials for Test Runs and sandbox DB queries"
            title="Use separate credentials for non-production DB queries"
          />
          {enableNonProdConfigs ? (
            <EnvironmentTabs
              prodContent={
                <RedshiftEnvironmentConfig
                  environment="production"
                  labelsPrefix="Production"
                />
              }
              prodError={Boolean(errors.productionConfig)}
              sandboxContent={
                <RedshiftEnvironmentConfig
                  environment="sandbox"
                  labelsPrefix="Sandbox"
                />
              }
              sandboxError={Boolean(errors.sandboxConfig)}
            />
          ) : (
            <RedshiftEnvironmentConfig
              environment="production"
              labelsPrefix=""
            />
          )}
          <DatabasePermissionsPill />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="redshift-connection-save"
              disabled={isSubmitting}
              htmlType="submit"
              loading={isSubmitting}
              size="base"
              variant="primary"
            >
              Save
            </Button>
          }
        ></Modal.Footer>
      </form>
    </FormProvider>
  );
};
